import { ABP, ListService, PagedResultDto, TrackByService } from '@abp/ng.core';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { DateAdapter } from '@abp/ng.theme.shared/extensions';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import type {
  GetEventTypesInput,
  EventTypeDto,
} from '../../proxy/event-types/models';
import { EventTypeService } from '../../proxy/event-types/event-type.service';

@Component({
  selector: 'app-event-type',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [ListService, { provide: NgbDateAdapter, useClass: DateAdapter }],
  templateUrl: './event-type.component.html',
  styles: [],
})
export class EventTypeComponent implements OnInit {
  data: PagedResultDto<EventTypeDto> = {
    items: [],
    totalCount: 0,
  };

  filters = {} as GetEventTypesInput;

  form: FormGroup;

  isFiltersHidden = true;

  isModalBusy = false;

  isModalOpen = false;

  selected?: EventTypeDto;

  constructor(
    public readonly list: ListService,
    public readonly track: TrackByService,
    public readonly service: EventTypeService,
    private confirmation: ConfirmationService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    const getData = (query: ABP.PageQueryParams) =>
      this.service.getList({
        ...query,
        ...this.filters,
        filterText: query.filter,
      });

    const setData = (list: PagedResultDto<EventTypeDto>) => (this.data = list);

    this.list.hookToQuery(getData).subscribe(setData);
  }

  clearFilters() {
    this.filters = {} as GetEventTypesInput;
  }

  buildForm() {
    const {
      name,
      description,
      code,
    } = this.selected || {};

    this.form = this.fb.group({
      name: [name ?? null, [Validators.required]],
      description: [description ?? null, []],
      code: [code ?? null, [Validators.required]],
    });
  }

  hideForm() {
    this.isModalOpen = false;
    this.form.reset();
  }

  showForm() {
    this.buildForm();
    this.isModalOpen = true;
  }

  submitForm() {
    if (this.form.invalid) return;

    const request = this.selected
      ? this.service.update(this.selected.id, this.form.value)
      : this.service.create(this.form.value);

    this.isModalBusy = true;

    request
      .pipe(
        finalize(() => (this.isModalBusy = false)),
        tap(() => this.hideForm()),
      )
      .subscribe(this.list.get);
  }

  create() {
    this.selected = undefined;
    this.showForm();
  }

  update(record: EventTypeDto) {
    this.selected = record;
    this.showForm();
  }

  delete(record: EventTypeDto) {
    this.confirmation.warn(
      'Analysis::DeleteConfirmationMessage',
      'Analysis::AreYouSure',
      { messageLocalizationParams: [] }
    ).pipe(
      filter(status => status === Confirmation.Status.confirm),
      switchMap(() => this.service.delete(record.id)),
    ).subscribe(this.list.get);
  }
}
